import type { NewsSource, PremarketPayload } from "./types";
import type { CaEntry, CaInfo, Candidate } from "./types";

const apiBase = "/api";

export type Provider = "mock" | "external" | "yahoo" | "set";

// Called whenever any API call comes back 401 — lets the app drop straight to login.
let unauthorizedHandler: (() => void) | null = null;
export const setUnauthorizedHandler = (handler: () => void) => {
  unauthorizedHandler = handler;
};

const request = async (path: string, init?: RequestInit) => {
  const response = await fetch(`${apiBase}${path}`, init);
  if (response.status === 401 && path !== "/login") {
    unauthorizedHandler?.();
  }
  return response;
};

// Verify the auth cookie against the server (source of truth, not localStorage).
export const verifyAuth = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${apiBase}/me`);
    return response.ok;
  } catch {
    return false;
  }
};

export const login = async (code: string): Promise<boolean> => {
  const response = await fetch(`${apiBase}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code })
  });
  return response.ok;
};

export const fetchPremarket = async (provider: Provider) => {
  const response = await request(`/premarket?provider=${provider}`);
  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error ?? `API error ${response.status}`);
  }

  return (await response.json()) as PremarketPayload;
};

export const fetchStock = async (symbol: string) => {
  const response = await request(`/stock/${encodeURIComponent(symbol)}`);
  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error ?? `API error ${response.status}`);
  }

  return (await response.json()) as {
    generatedAt: string;
    sourceMode: "yahoo";
    stock: Candidate;
  };
};

export type IntradayPoint = { t: string; price: number; volume: number; vwap: number };
export type DailyPoint = { date: string; close: number; high: number; low: number; volume: number };

export type StockDetail = {
  symbol: string;
  marketStatus: string;
  live: boolean;
  priorClose: number;
  open: number;
  last: number;
  vwapLast: number | null;
  orderBook: { bidPrice: number; bidVolume: number; offerPrice: number; offerVolume: number };
  intraday: IntradayPoint[];
  daily: DailyPoint[];
  ca?: CaInfo | null;
  corporateActions?: CaEntry[];
};

export const fetchStockDetail = async (symbol: string) => {
  const response = await request(`/stock/${encodeURIComponent(symbol)}/detail`);
  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error ?? `API error ${response.status}`);
  }

  return (await response.json()) as StockDetail;
};

export const fetchNewsSources = async () => {
  const response = await request(`/news-sources`);
  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error ?? `API error ${response.status}`);
  }

  return (await response.json()) as {
    generatedAt: string;
    sources: NewsSource[];
  };
};
